import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const EFFECTIVE = "October 8, 2026";
const CONTACT = "hello@findleystmedia.com";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-medium tracking-[-0.01em] text-white md:text-2xl">{title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-fog">{children}</div>
  </section>
);

const PRIVACY = {
  title: "Privacy Policy",
  intro:
    "This policy explains what Findley St Media collects when you use our website, purchase a build, or complete onboarding — and what we do with it.",
  sections: [
    {
      title: "What we collect",
      body: [
        "Project inquiries: your name, business name, email, phone number, project details, links, and any files you choose to upload through our Start a Project intake.",
        "Purchases and onboarding: your contact details, business information, customization requests, and brand assets you provide. Payment details are collected and processed directly by Stripe — we never see or store your full card number.",
        "We do not run advertising trackers or sell your information. Our site uses a single secure cookie solely to keep authorized administrators signed in.",
      ],
    },
    {
      title: "How we use it",
      body: [
        "To respond to inquiries, fulfill orders, customize and launch your build, operate active subscriptions, and meet legal and accounting obligations.",
        "Order and subscription records are retained while your service is active and as required for our records thereafter.",
      ],
    },
    {
      title: "Who processes it",
      body: [
        "Stripe processes payments. Transactional email is sent through our email delivery provider. Files and data are stored with our cloud hosting and storage providers. These processors handle data only to provide their services to us.",
        "We do not sell, rent, or share your personal information with third parties for their own marketing.",
      ],
    },
    {
      title: "Your choices",
      body: [
        `You may request a copy, correction, or deletion of your personal information at any time by emailing ${CONTACT}. Deletion requests are honored except where records must be retained for legal, tax, or accounting purposes.`,
      ],
    },
    {
      title: "Security",
      body: [
        "Payment pages are served by Stripe over encrypted connections. Order records and uploaded files are stored behind authenticated access controls. Onboarding links are unique, unguessable, and released only after a verified payment.",
      ],
    },
    {
      title: "Changes and contact",
      body: [
        `If this policy changes, the updated version will be posted here with a new effective date. Questions: ${CONTACT}.`,
      ],
    },
  ],
};

const TERMS = {
  title: "Purchase & Subscription Terms",
  intro:
    "These terms govern purchases of Findley St builds — Own It (one-time) and Website as a Service (monthly). Paying through our checkout constitutes acceptance.",
  sections: [
    {
      title: "What you're buying",
      body: [
        "Each showroom product is an existing Findley St build — its design and functionality — customized to your business, branding, services, content, imagery, and contact details, then launched. You are purchasing the underlying build as a starting point, not the original client's brand or content.",
        "The purchase covers adapting the existing build. Functionality beyond the existing product's scope may require a separate written quote.",
      ],
    },
    {
      title: "Own It (one-time purchase)",
      body: [
        "One payment at the listed price. Includes initial customization of the existing build and initial launch work. On delivery, the customized website or system is yours, subject to the licenses of any third-party components it uses.",
        "Hosting is not included in the one-time price and is arranged directly with you after purchase.",
      ],
    },
    {
      title: "Website as a Service (monthly subscription)",
      body: [
        "Billed monthly at the listed price through Stripe. Includes initial customization, initial launch, and hosting as part of the service. No setup fee. No minimum term.",
        "Your website or system remains live while the subscription stays active. If a payment fails, we'll notify you and allow a reasonable period to update payment before service is interrupted.",
        "Minor content changes may be accommodated as part of the service; larger changes may require a separate quote.",
      ],
    },
    {
      title: "Cancellation (30-day notice)",
      body: [
        "You may request cancellation at any time through the Manage page using your purchase email and order reference. Cancellation takes effect at your next billing date when requested at least 30 days in advance.",
        "If your next billing date falls inside the 30-day window, that payment is still charged and cancellation takes effect at the following billing date. Requests are acknowledged with your next billing date, effective date, and any remaining billing obligation before you confirm.",
        "Submitting a cancellation request never takes your website offline by itself; the transition is coordinated with you.",
      ],
    },
    {
      title: "Buyout",
      body: [
        "A Website as a Service customer may purchase the build outright later at the full Own It price in effect at the time of buyout. Previous monthly payments do not count toward the purchase price.",
        "Buyouts are handled as a direct request and a one-time purchase, with the subscription transition and hosting arrangements coordinated so your live website is not prematurely disabled.",
      ],
    },
    {
      title: "Payments",
      body: [
        "Prices are listed in US dollars and charged through Stripe's secure checkout. Applicable taxes are calculated and collected at checkout where required.",
        "Chargebacks or payment disputes should be raised with us first at " + CONTACT + " so we can resolve them directly.",
      ],
    },
    {
      title: "Your content",
      body: [
        "You confirm you have the rights to any logos, images, text, and other assets you provide, and you permit us to use them to build and operate your website.",
      ],
    },
    {
      title: "Delivery and follow-up",
      body: [
        "After payment you'll complete a short onboarding so we can customize your build. We review every order personally and follow up about customization and launch. No delivery timeline is guaranteed at checkout.",
      ],
    },
    {
      title: "Liability",
      body: [
        "To the maximum extent permitted by law, Findley St Media's total liability for any claim relating to a purchase is limited to the amount you paid for the product giving rise to the claim. We are not liable for indirect or consequential losses.",
      ],
    },
    {
      title: "Changes and contact",
      body: [
        `These terms may be updated; the version posted here at the time of your purchase governs that purchase. Questions: ${CONTACT}.`,
      ],
    },
  ],
};

export default function Legal({ doc }) {
  const content = doc === "terms" ? TERMS : PRIVACY;
  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Seo
        title={content.title}
        description={`${content.title} for findleystmedia.com — how Findley St Media handles ${doc === "terms" ? "purchases, subscriptions and service terms" : "your information"}.`}
        path={doc === "terms" ? "/terms" : "/privacy"}
      />
      <div className="mx-auto max-w-2xl px-5 pb-32 pt-32 md:px-10 md:pt-44">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <Link to="/work" data-testid="legal-back-link" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-0.5" />
            Findley St
          </Link>
          <h1 className="mt-10 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-5xl" data-testid="legal-title">
            {content.title}
          </h1>
          <p className="mt-3 text-[10px] uppercase tracking-[0.35em] text-fog/50">Effective {EFFECTIVE}</p>
          <p className="mt-6 text-sm leading-relaxed text-fog md:text-base">{content.intro}</p>
          {content.sections.map((s) => (
            <Section key={s.title} title={s.title}>
              {s.body.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </Section>
          ))}
          <p className="mt-14 border-t border-white/10 pt-6 text-xs leading-relaxed text-fog/50">
            {doc === "terms" ? (
              <>Related: <Link to="/privacy" className="underline underline-offset-4 transition-colors hover:text-white">Privacy Policy</Link></>
            ) : (
              <>Related: <Link to="/terms" className="underline underline-offset-4 transition-colors hover:text-white">Purchase &amp; Subscription Terms</Link></>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
